import { FC, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { NoContractSection } from './Section';
import { INoContractContentProps, NoContractForm, NoContractStep } from './types';
import { TableUnrecognizedService } from './TableUnrecognizedService';
import { NoContractContentStep2Reject } from './ContentStep2Reject';
import { NoContractContentStep3 } from './ContentStep3';
import { NoContractContentStep2Create } from './ContentStep2Create';
import { NoContractContentStep2Accept } from './ContentStep2Accept';
import { NoContractContentStep1 } from './ContentStep1';
import { NoContractContentStep2NewService } from './ContentStep2NewService';

export const NoContractContent: FC<INoContractContentProps> = (props) => {
  const { activeStep, matchingService, draft, isLoadingCharges, billedEntityId, onSuccessSubmit, onEditingChargeChange } = props;
  const { watch } = useFormContext<NoContractForm>();
  const { verdict } = watch();

  // Get mismatched fields from first partialMatchingService and merge with contract-level mismatches
  const mismatchedFields = useMemo(() => {
    const firstPartialService = matchingService?.partialMatchingServices?.[0];
    if (!firstPartialService) return [];

    // Get service-level mismatched fields
    const serviceMismatches = firstPartialService.mismatchedFields || [];

    // Get contract-level mismatched fields from all contractInfos
    const contractMismatches = firstPartialService.contractInfos?.flatMap((contract) => contract.mismatchedFields || []) || [];

    // Combine and deduplicate
    return [...new Set([...serviceMismatches, ...contractMismatches])];
  }, [matchingService?.partialMatchingServices]);

  return (
    <>
      {/* Hide Shipment Details when in Step 2b - New Service and Step 3 - Summary */}
      {activeStep !== NoContractStep.Step2b_NewService && activeStep !== NoContractStep.Step3_Summary && (
        <div className='p-24 border-y-1 border-[#DFDFDF]'>
          <NoContractSection title='Shipment Details' description='Service Details read from the Shipment'>
            <TableUnrecognizedService
              data={[matchingService?.unmatchedService]}
              mismatchedFields={activeStep === NoContractStep.Step1_Select ? mismatchedFields : []}
            />
          </NoContractSection>
        </div>
      )}

      {activeStep === NoContractStep.Step1_Select && <NoContractContentStep1 onSuccessSubmit={onSuccessSubmit} billedEntityId={billedEntityId} />}
      {activeStep === NoContractStep.Step2_Resolution && verdict === 'reject' && <NoContractContentStep2Reject />}

      {activeStep === NoContractStep.Step2_Resolution && verdict === 'setup-new' && (
        <NoContractContentStep2Create isLoadingCharges={isLoadingCharges} isEditable draft={draft} matchingService={matchingService} activeStep={activeStep} />
      )}
      {activeStep === NoContractStep.Step2b_NewService && verdict === 'setup-new' && (
        <NoContractContentStep2NewService
          isLoadingCharges={isLoadingCharges}
          isEditable
          draft={draft}
          matchingService={matchingService}
          activeStep={activeStep}
          onEditingChargeChange={onEditingChargeChange}
        />
      )}

      {activeStep === NoContractStep.Step2_Resolution && verdict === 'accept' && <NoContractContentStep2Accept />}

      {activeStep === NoContractStep.Step3_Summary && <NoContractContentStep3 draft={draft} matchingService={matchingService} activeStep={activeStep} />}
    </>
  );
};
